/**
 * @file contains helper functions for Compose List Search component
 */
import filter from '../../../../utils/filter';

import { DataType } from './types';

/**
 * Get whether the list is empty (nothing to show)
 *
 * @param data The data that are able to be assigned
 * @returns    Whether it's true
 */
export const getIsEmpty = (data: DataType[]): boolean => {
  return data.length === 0;
};

/**
 * Get whether the user is searching groups and members
 * (whitespace doesn't count)
 *
 * @param searchTerm What is typed in the search form
 * @returns          Whether it's true
 */
export const getIsSearching = (searchTerm: string): boolean => {
  return searchTerm.toString().trim().length !== 0;
};

/**
 * Get whether to show no results text
 *
 * @param object             Object containing all the necessary data for calculating whether to show no results text
 * @param object.isEmpty     Whether the assignment modal is empty (no groups or members to show)
 * @param object.isLoading   Whether the data is being fetched
 * @param object.isSearching Whether the user is searching groups and members
 * @returns                  Whether to show no results text
 */
export const getShouldShowNoResults = ({
  isEmpty,
  isLoading,
  isSearching,
}: {
  isEmpty: boolean;
  isLoading: boolean;
  isSearching: boolean;
}): boolean => {
  return isEmpty === true && isLoading === false && isSearching === false;
};

/**
 * Filter passed data by title
 *
 * @param data   All data
 * @param search What the user typed in search box
 * @returns      Filtered array of data
 */
export const searchDataByTitle = <T>(data: T[], search: string): T[] => {
  // If nothing is searched, then just return everything
  if (search.toString().trim().length === 0) {
    return data;
  }

  return filter(data, search, { keys: ['title'] });
};
